import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Loader,
  Alert,
  Button,
  Avatar,
  Divider,
  TextInput,
  ActionIcon
} from '@mantine/core';
import {
  IconMessage,
  IconSearch,
  IconRefresh,
  IconAlertCircle,
  IconInbox,
  IconUser
} from '@tabler/icons-react';
import { api } from '../services/api';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    // Only fetch messages if authenticated (ProtectedRoute handles redirect)
    if (isAuthenticated) {
      fetchMessages();
    }
  }, [isAuthenticated]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getMessages();
      
      // Group messages by conversation
      const convos = {};
      response.messages.forEach(msg => {
        const otherUserId = msg.sender === user.id ? msg.recipient : msg.sender;
        const otherUsername = msg.sender === user.id ? msg.recipient_username : msg.sender_username;
        
        if (!convos[otherUserId]) {
          convos[otherUserId] = {
            userId: otherUserId,
            username: otherUsername,
            lastMessage: msg,
            unreadCount: 0,
            messages: []
          };
        }
        
        convos[otherUserId].messages.push(msg);
        
        // Update last message if this one is newer
        if (new Date(msg.created_at) > new Date(convos[otherUserId].lastMessage.created_at)) {
          convos[otherUserId].lastMessage = msg;
        }
        
        // Count unread messages
        if (!msg.is_read && msg.recipient === user.id) {
          convos[otherUserId].unreadCount++;
        }
      });
      
      // Convert to array and sort by last message time
      const conversationList = Object.values(convos).sort((a, b) => 
        new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
      );
      
      setConversations(conversationList);
      setMessages(response.messages);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(convo =>
    convo.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <ProtectedRoute requireAuth={true} redirectTo="/">
        <Container size="md" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/">
      <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconInbox size={32} />
            <Title order={1}>Messages</Title>
          </Group>
          <ActionIcon 
            onClick={fetchMessages} 
            variant="light"
            size="lg"
            disabled={loading}
          >
            <IconRefresh size="1.2rem" />
          </ActionIcon>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {error}
          </Alert>
        )}

        {/* Search */}
        <TextInput
          placeholder="Search conversations..."
          leftSection={<IconSearch size="1rem" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
        />

        {/* Conversations List */}
        <Paper shadow="sm" radius="md" withBorder>
          {filteredConversations.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <IconMessage size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
              <Text c="dimmed" size="lg">
                {searchTerm ? 'No conversations found' : 'No messages yet'}
              </Text>
              <Text c="dimmed" size="sm" mt="xs">
                {!searchTerm && 'Start a conversation by messaging a princess!'}
              </Text>
            </div>
          ) : (
            <Stack gap={0}>
              {filteredConversations.map((convo, index) => (
                <div key={convo.userId}>
                  <div
                    style={{
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => navigate(`/messages/${convo.userId}`)}
                  >
                    <Group gap="md" align="flex-start">
                      <Avatar size="md" color="blue">
                        <IconUser size="1.5rem" />
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <Group justify="space-between" mb={4}>
                          <Group gap="xs">
                            <Text fw={convo.unreadCount > 0 ? 700 : 500}>
                              {convo.username}
                            </Text>
                            {convo.unreadCount > 0 && (
                              <Badge color="red" size="sm" circle>
                                {convo.unreadCount}
                              </Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {formatMessageTime(convo.lastMessage.created_at)}
                          </Text>
                        </Group>
                        <Text 
                          size="sm" 
                          c={convo.unreadCount > 0 ? undefined : "dimmed"}
                          lineClamp={1}
                          fw={convo.unreadCount > 0 ? 500 : 400}
                        >
                          {convo.lastMessage.sender === user.id && 'You: '}
                          {convo.lastMessage.content}
                        </Text>
                      </div>
                    </Group>
                  </div>
                  {index < filteredConversations.length - 1 && <Divider />}
                </div>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
    </ProtectedRoute>
  );
};

export default MessagesPage;